'use client'

import { useEffect, useRef } from 'react'

/**
 * A soft cornflower light that follows the presenter's pointer, so a room can
 * see where they are pointing (plan 08 WP-VIS-03, ported from Driver-Auto-CC).
 *
 * Accessibility guard:
 * - listeners attach only for a fine, hovering pointer and never under
 *   `prefers-reduced-motion: reduce`; both queries are subscribed, so changing
 *   either mid-session removes or restores the effect without a reload;
 * - `pointer-events: none` (in `.pointer-glow`), so it never takes a click,
 *   hover, drag or text selection;
 * - `aria-hidden` and outside the focus order;
 * - hidden under forced colours and in print (globals.css);
 * - one paint per animation frame and no React state per move.
 *
 * Colours and reach come from the `--glow-*` tokens. Layer: z 40, under the
 * header, the rail and every overlay.
 */
export function PointerGlow() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node || typeof window.matchMedia !== 'function') return

    const fine = window.matchMedia('(any-hover: hover) and (any-pointer: fine)')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    let x = 0
    let y = 0
    let frame: number | null = null
    let attached = false

    const paint = () => {
      frame = null
      node.style.setProperty('--glow-x', `${x}px`)
      node.style.setProperty('--glow-y', `${y}px`)
      node.style.opacity = '1'
    }
    const onMove = (event: MouseEvent) => {
      x = event.clientX
      y = event.clientY
      if (frame === null) frame = window.requestAnimationFrame(paint)
    }
    const onLeave = () => {
      node.style.opacity = '0'
    }
    const detach = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseleave', onLeave)
      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = null
      node.style.opacity = '0'
      attached = false
    }
    const sync = () => {
      const want = fine.matches && !reduced.matches
      if (want && !attached) {
        document.addEventListener('mousemove', onMove, { passive: true })
        document.addEventListener('mouseleave', onLeave)
        attached = true
      } else if (!want && attached) {
        detach()
      }
    }

    sync()
    fine.addEventListener('change', sync)
    reduced.addEventListener('change', sync)
    return () => {
      fine.removeEventListener('change', sync)
      reduced.removeEventListener('change', sync)
      if (attached) detach()
    }
  }, [])

  return <div ref={ref} aria-hidden='true' className='pointer-glow' />
}

/**
 * Presenter cut switch: `NEXT_PUBLIC_POINTER_GLOW=off` removes the glow.
 * Inlined at build time, like every NEXT_PUBLIC_* value.
 */
export const POINTER_GLOW_ENABLED = process.env.NEXT_PUBLIC_POINTER_GLOW !== 'off'
